'use client';

import { c as _c } from "react/compiler-runtime";
import { formatLongString } from '@morpho-org/morpho-ts';
import { DataList } from '@morpho-org/ui-v6/client';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { parseEther } from 'viem';
import type { Warning } from '@/components/common/WarningIcon/WarningHoverCard/WarningHoverCardContent';
import { WarningIcon } from '@/components/common/WarningIcon';
import { getLiquidationPenalty } from '@/helpers/incentiveFactor';
import { getMarketWarningMessage } from '@/helpers/warningMessages';
import { MarketWarningType } from '@/helpers/warnings';
import { WarningLevel } from '@/types/enums';
import type { MarketLiquidationDetail } from './liquidationDetail.contract';
const percentageFormatter = formatters.percent.digits(2).unit('%').trailingZero(false).default('-');
const assetFormatter = formatters.short.unit('').default('-');
const WARNING_LEVELS = {
  red: WarningLevel.Red,
  yellow: WarningLevel.Yellow
} as const;
function isWarningLevel(level: string): level is keyof typeof WARNING_LEVELS {
  return Object.hasOwn(WARNING_LEVELS, level);
}
function toBadDebtWarning(warning: MarketLiquidationDetail['unrealizedBadDebtWarning'], loanToken: MarketLiquidationDetail['loanToken']): Warning | null {
  if (!warning || !isWarningLevel(warning.level)) return null;
  const message = getMarketWarningMessage({
    type: MarketWarningType.BAD_DEBT_UNREALIZED,
    level: warning.level,
    metadata: {
      badDebtAssets: warning.badDebtAssets,
      badDebtUsd: warning.badDebtUsd
    }
  }, {
    symbol: loanToken.symbol ?? undefined,
    decimals: loanToken.decimals
  });
  return message ? {
    message,
    level: WARNING_LEVELS[warning.level]
  } : null;
}
export interface LiquidationDetailDataListClientProps {
  detail: MarketLiquidationDetail;
}
const LiquidationDetailDataListClient = t0 => {
  const $ = _c(46);
  const {
    detail
  } = t0;
  const {
    loanToken
  } = detail;
  let T0;
  let T1;
  let T2;
  let T3;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== detail.lltv || $[1] !== detail.realizedBadDebt || $[2] !== detail.unrealizedBadDebt || $[3] !== detail.unrealizedBadDebtWarning || $[4] !== loanToken) {
    const lltv = BigInt(detail.lltv);
    const priceWad = loanToken.priceUsd === null ? undefined : parseEther(loanToken.priceUsd.toFixed(18));
    const symbol = formatLongString(loanToken.symbol ?? "", 10);
    const formatBadDebt = badDebt => {
      const assets = badDebt ? BigInt(badDebt.assets) : undefined;
      return assetFormatter.unit(symbol).digits(getDigitsFromPriceAndAmount(priceWad, assets, loanToken.decimals)).of(assets, loanToken.decimals);
    };
    let t6;
    if ($[14] !== detail.unrealizedBadDebtWarning || $[15] !== loanToken) {
      t6 = toBadDebtWarning(detail.unrealizedBadDebtWarning, loanToken);
      $[14] = detail.unrealizedBadDebtWarning;
      $[15] = loanToken;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    const unrealizedBadDebtWarning = t6;
    T3 = DataList.Root;
    t4 = true;
    let t7;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <DataList.LeftItem><span className="text-secondary-foreground text-xs">Liquidation Loan-To-Value (LLTV)</span></DataList.LeftItem>;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    const t8 = <DataList.Line>{t7}<DataList.RightItem label={percentageFormatter.of(lltv, 18)} /></DataList.Line>;
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <DataList.LeftItem><div className="flex items-center gap-[6px]"><span className="text-secondary-foreground text-xs">Realized Bad Debt</span></div></DataList.LeftItem>;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    const t10 = <DataList.Line>{t9}<DataList.RightItem label={formatBadDebt(detail.realizedBadDebt)} /></DataList.Line>;
    if ($[19] !== t10 || $[20] !== t8) {
      t5 = <DataList.Column>{t8}{t10}</DataList.Column>;
      $[19] = t10;
      $[20] = t8;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    T2 = DataList.Column;
    let t11;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <DataList.LeftItem><span className="text-secondary-foreground text-xs">Liquidation Penalty</span></DataList.LeftItem>;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    const t12 = percentageFormatter.of(getLiquidationPenalty(lltv), 18);
    if ($[23] !== t12) {
      t3 = <DataList.Line>{t11}<DataList.RightItem label={t12} /></DataList.Line>;
      $[23] = t12;
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    T1 = DataList.Line;
    let t13;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <span className="text-secondary-foreground text-xs">Unrealized Bad Debt</span>;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== unrealizedBadDebtWarning) {
      t14 = unrealizedBadDebtWarning && <WarningIcon warnings={[unrealizedBadDebtWarning]} isSimple={true} />;
      $[26] = unrealizedBadDebtWarning;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    if ($[28] !== t14) {
      t2 = <DataList.LeftItem><div className="flex items-center gap-[6px]">{t13}{t14}</div></DataList.LeftItem>;
      $[28] = t14;
      $[29] = t2;
    } else {
      t2 = $[29];
    }
    T0 = DataList.RightItem;
    t1 = formatBadDebt(detail.unrealizedBadDebt);
    $[0] = detail.lltv;
    $[1] = detail.realizedBadDebt;
    $[2] = detail.unrealizedBadDebt;
    $[3] = detail.unrealizedBadDebtWarning;
    $[4] = loanToken;
    $[5] = T0;
    $[6] = T1;
    $[7] = T2;
    $[8] = T3;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    T0 = $[5];
    T1 = $[6];
    T2 = $[7];
    T3 = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[30] !== T0 || $[31] !== t1) {
    t6 = <T0 label={t1} />;
    $[30] = T0;
    $[31] = t1;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== T1 || $[34] !== t2 || $[35] !== t6) {
    t7 = <T1>{t2}{t6}</T1>;
    $[33] = T1;
    $[34] = t2;
    $[35] = t6;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== T2 || $[38] !== t3 || $[39] !== t7) {
    t8 = <T2>{t3}{t7}</T2>;
    $[37] = T2;
    $[38] = t3;
    $[39] = t7;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== T3 || $[42] !== t4 || $[43] !== t5 || $[44] !== t8) {
    t9 = <T3 fullWidth={t4}>{t5}{t8}</T3>;
    $[41] = T3;
    $[42] = t4;
    $[43] = t5;
    $[44] = t8;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  return t9;
};
export default LiquidationDetailDataListClient;